'use client'

import Image from 'next/image'
import Link from 'next/link'
import { TbDeviceSim, TbShieldCheck, TbMapPin, TbRoad, TbCar, TbMotorbike, TbSailboat, TbBuildingCommunity, TbShieldPlus } from 'react-icons/tb'
import { buildProviderLink } from '@/utils/blog/providerLinkHelpers'
import AffiliateLink from '@/components/common/AffiliateLink'
import { forReaderLanguage } from '@/lib/affiliates/readerPayment'
import { providerCityToursUrl } from '@/lib/tours/cityTourLinks'
import { OfferView } from '@/lib/affiliates/trackOfferView'

/**
 * Контекстный партнёрский оффер внутри статьи (после интро).
 *
 * Зачем: блок «Рекомендуемые сервисы» стоит в самом низу статьи и почти не
 * кликается (77% трафика — мобильный, до низа не докручивают). Эта карточка
 * ставится сразу после вводного абзаца и показывает ОДИН релевантный оффер под
 * интент страницы. Данные берутся из уже загруженного пресета (без доп. запросов),
 * клик трекается через AffiliateLink (событие affiliate_click, position="blog-inline").
 *
 * Рендерит null, если для вертикала нет провайдера или не строится ссылка —
 * поэтому безопасен для любой статьи.
 */

// Вертикал → поле пресета.
const VERTICAL_FIELD = {
    esim: 'esimItems',
    vpn: 'vpnItems',
    tours: 'toursItems',
    transfer: 'transferItems',
    carRental: 'carRentalItems',
    bike: 'bikeItems',
    boat: 'boatItems',
    stay: 'stayItems',
    insurance: 'insuranceItems',
}

const VERTICAL_ICON = {
    esim: TbDeviceSim,
    vpn: TbShieldCheck,
    tours: TbMapPin,
    transfer: TbRoad,
    carRental: TbCar,
    bike: TbMotorbike,
    boat: TbSailboat,
    stay: TbBuildingCommunity,
    insurance: TbShieldPlus,
}

// Локализованные подписи: eyebrow (над карточкой) и текст кнопки.
const LABELS = {
    esim: { ru: { eyebrow: 'Интернет в поездке', cta: 'Оформить eSIM' }, en: { eyebrow: 'Stay connected', cta: 'Get an eSIM' } },
    vpn: { ru: { eyebrow: 'Доступ к заблокированному', cta: 'Выбрать VPN' }, en: { eyebrow: 'Unblock apps abroad', cta: 'Get a VPN' } },
    tours: { ru: { eyebrow: 'Экскурсии и впечатления', cta: 'Смотреть экскурсии' }, en: { eyebrow: 'Tours & experiences', cta: 'Browse tours' } },
    transfer: { ru: { eyebrow: 'Трансфер без переплат', cta: 'Заказать трансфер' }, en: { eyebrow: 'Airport transfer', cta: 'Book a transfer' } },
    carRental: { ru: { eyebrow: 'Аренда авто', cta: 'Найти авто' }, en: { eyebrow: 'Car rental', cta: 'Find a car' } },
    bike: { ru: { eyebrow: 'Скутеры и байки', cta: 'Выбрать' }, en: { eyebrow: 'Bikes & scooters', cta: 'Browse' } },
    boat: { ru: { eyebrow: 'Лодки и яхты', cta: 'Смотреть' }, en: { eyebrow: 'Boats & yachts', cta: 'Browse' } },
    stay: { ru: { eyebrow: 'Где остановиться', cta: 'Подобрать жильё' }, en: { eyebrow: 'Where to stay', cta: 'Find a stay' } },
    insurance: { ru: { eyebrow: 'Страховка для поездки', cta: 'Оформить страховку' }, en: { eyebrow: 'Travel insurance', cta: 'Get insurance' } },
}

// Контекстная внутренняя ссылка на профильный car-rental гайд страны.
// Зачем: гайды почти не получают внутренних ссылок (georgia-guide вообще был
// «Discovered — not indexed», without-deposit — поз. 44 в GSC 2026-07), а
// авто-смежные посты (границы/парковки/дороги, ~500 кликов/квартал) — главные
// доноры веса и трафика под этот интент.
const CAR_RENTAL_GUIDE = {
    montenegro: 'car-rental-montenegro-without-deposit',
    georgia: 'car-rental-georgia-guide',
    albania: 'car-rental-in-albania',
    qatar: 'car-rental-in-qatar-where-to-book-prices-and-hidden-pitfalls',
}

const GUIDE_LABEL = {
    ru: 'Полный гайд по аренде авто →',
    en: 'Full car rental guide →',
}

const PROVIDER_PREFERENCE = {
    'scooter-motorcycle-rental-montenegro': 'bikesbooking',
    'how-to-use-buses-in-montenegro': 'omio',
    'tax-free-in-qatar': 'trip',
}

/**
 * Нормализует benefits в массив коротких строк. Все 26 провайдеров хранят преимущества
 * объектами {title, description: [блоки]} — старый разбор ждал description строкой, и
 * блок преимуществ карточки был пуст на всём сайте (исследование 30.09.2026, L04).
 * Берём title: в чипе нужна короткая формулировка, а не абзац.
 */
function normalizeBenefits(provider, language) {
    const raw = language === 'ru'
        ? (provider.benefitsRu || provider.benefitsEn || [])
        : (provider.benefitsEn || provider.benefitsRu || [])
    return (raw || [])
        .map((item) => {
            if (!item) return null
            if (typeof item === 'string') return item
            if (typeof item === 'object' && typeof item.title === 'string' && item.title.trim()) return item.title.trim()
            if (typeof item === 'object' && typeof item.description === 'string') return item.description
            if (Array.isArray(item.children)) {
                return item.children.map((c) => (typeof c.text === 'string' ? c.text : '')).join(' ').trim()
            }
            return null
        })
        .filter((t) => typeof t === 'string' && t.trim().length > 0)
}

// position прокидывается в subid и в affiliate_click: 'blog-inline' (дефолт, статьи)
// или 'poi-inline'/'poi-getting' (карточки в шаблоне POI, добавлены 2026-08-13 —
// аудит §8: 29% AI-сессий садились на /places без единого оффера).
export default function InlineOfferCard({ preset, vertical, language = 'ru', country = null, postSlug = null, position = 'blog-inline', citySlug = null, cityViator = null }) {
    if (!preset || !vertical) return null

    const items = preset[VERTICAL_FIELD[vertical]]
    const activeProviders = forReaderLanguage(Array.isArray(items)
        ? items.filter((i) => i?._type === 'affiliateProvider' && i.active === true)
        : [], language)
    const preferredCode = PROVIDER_PREFERENCE[postSlug]
    const provider = activeProviders.find((item) => item.code === preferredCode) || activeProviders[0] || null
    if (!provider) return null

    // На странице места карточка туров вела в каталог всей страны (Hohematte → вся Швейцария,
    // Гвелети → топ Тбилиси) — при известном городе берём его страницу у провайдера (обход 01.10.2026).
    const cityHref = vertical === 'tours'
        ? providerCityToursUrl(provider, { citySlug, country, viatorRef: cityViator, utmTerm: postSlug ? `poi-${postSlug}` : '' })
        : null
    const href = cityHref || buildProviderLink(provider, language, country, {
        pageSlug: postSlug,
        position,
    })
    if (!href || href === '#') return null

    const label = LABELS[vertical]?.[language] || LABELS[vertical]?.en || { eyebrow: '', cta: language === 'ru' ? 'Подробнее' : 'Learn more' }
    const Icon = VERTICAL_ICON[vertical] || TbMapPin

    // Гайд-ссылка только для carRental и не на самом гайде (без self-link).
    const guideSlug = vertical === 'carRental' ? CAR_RENTAL_GUIDE[country] : null
    const showGuideLink = Boolean(guideSlug) && guideSlug !== postSlug
    const title = provider.title || provider.code
    const description = language === 'ru'
        ? (provider.descriptionRu || provider.descriptionEn)
        : (provider.descriptionEn || provider.descriptionRu)
    const benefits = normalizeBenefits(provider, language)

    return (
        <OfferView as="aside" className="gf-inline-offer" aria-label={label.eyebrow} provider={provider.code} vertical={vertical} country={country} position={position}>
            <div className="gf-inline-offer-eyebrow">
                <Icon aria-hidden="true" />
                <span>{label.eyebrow}</span>
            </div>
            <div className="gf-inline-offer-main">
                {provider.logoUrl && (
                    <div className="gf-inline-offer-logo">
                        <Image src={provider.logoUrl} alt={provider.logoAlt || title} width={44} height={44} className="object-contain" />
                    </div>
                )}
                <div className="gf-inline-offer-bd">
                    <h4>{title}</h4>
                    {description && <p>{description}</p>}
                    {benefits.length > 0 && (
                        <div className="gf-inline-offer-benefits">
                            {benefits.slice(0, 2).map((b, i) => <span key={i}>{b}</span>)}
                        </div>
                    )}
                </div>
                <AffiliateLink
                    href={href}
                    provider={provider.code}
                    vertical={vertical}
                    country={country}
                    position={position}
                    className="gf-btn gf-inline-offer-cta"
                >
                    {label.cta} →
                </AffiliateLink>
            </div>
            {showGuideLink && (
                <div className="gf-inline-offer-guide">
                    <Link href={`/${language}/blog/${guideSlug}`}>
                        {GUIDE_LABEL[language] || GUIDE_LABEL.en}
                    </Link>
                </div>
            )}
        </OfferView>
    )
}
